import React from "react";
import styled from "styled-components";
import Image from "next/image";
import { Carousel } from "antd";
import { useTranslation } from "next-i18next";
import { CommentCard } from "./CommentCard";

const ArrowLeftIcon = () => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M38.5 24H9.5"
      stroke="#202123"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M24 38.5L9.5 24L24 9.5"
      stroke="#202123"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.5 24H38.5"
      stroke="#202123"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M24 9.5L38.5 24L24 38.5"
      stroke="#202123"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MainContainer = styled.div`
  width: 100%;
  background-color: transparent;
  padding: 60px 16px 0 16px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 24px 0 24px 0;
  }
`;

const RatingContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 32px;
  width: 100%;
  max-width: 1388px;
  height: 321px;

  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  z-index: 0;

  @media (max-width: 1200px) {
    padding: 0 16px;
    gap: 24px;
    max-width: 375px;
    min-height: 181px;
    height: auto;
    margin: 0 auto;
  }
`;

const RatingBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 24px;
  width: 284px;
  height: 268px;

  flex: none;
  order: 0;
  flex-grow: 0;

  @media (max-width: 1200px) {
    width: 123px;
    gap: 8px;
    height: auto;
  }
`;

const RatingInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 8px;
  width: 284px;
  height: 136px;

  flex: none;
  order: 0;
  flex-grow: 0;

  @media (max-width: 1200px) {
    width: 123px;
    height: auto;
  }
`;

const RatingScore = styled.div`
  width: 103px;
  height: 71px;
  font-style: normal;
  font-weight: 600;
  font-size: 70px;
  line-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #202123;

  flex: none;
  order: 0;
  flex-grow: 0;

  @media (max-width: 1200px) {
    font-size: 50px;
    font-weight: 700;
    line-height: 1;
    width: auto;
    height: auto;
  }
`;

const StarRating = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;

  .mobile-stars {
    display: none;
  }

  &.star-lg {
    width: 284px;
    height: 57px;
    flex: none;
    order: 1;
    flex-grow: 0;

    img {
      width: 284px;
      height: 57px;
    }

    @media (max-width: 1200px) {
      width: auto;
      height: auto;

      .desktop-stars {
        display: none;
      }

      .mobile-stars {
        display: flex;
        flex-direction: row;
        gap: 2px;
      }
    }
  }
`;

const RatingHeadline = styled.div`
  width: 284px;
  height: 108px;
  font-size: 28px;
  line-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #202123;

  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;

  @media (max-width: 1200px) {
    width: auto;
    height: auto;
    font-size: 15px;
    line-height: 19px;
    font-weight: 600;
    margin-top: 0;
  }
`;

const TestimonialWrapper = styled.div`
  position: relative;
  width: 1064px;
  height: 321px;

  flex: none;
  order: 1;
  flex-grow: 0;

  @media (max-width: 1200px) {
    width: 196px;
    height: auto;
    flex: none;
  }
`;

const TestimonialContainer = styled.div`
  width: 1064px;
  height: 321px;
  overflow: hidden;

  @media (max-width: 1200px) {
    display: none;
  }

  .slick-list {
    margin: 0;
  }

  .slick-slide {
    padding: 0 8px;
  }

  .slick-slide .testimonial-card {
    opacity: 0.5;
    transition: opacity 0.3s ease;
  }

  .slick-slide.slick-active .testimonial-card {
    opacity: 1;
  }

  .slick-dots {
    margin: 24px 0 0 0;
    position: static;
    display: flex !important;
    justify-content: center;
    list-style: none;
    padding: 0;

    li {
      margin: 0 4px;
      width: 8px;
      height: 8px;
      background-color: #d9d9d6;
      border-radius: 5px;
      transition: width 0.3s ease, background-color 0.3s ease;

      button {
        display: none;
      }

      &.slick-active {
        background-color: #fa4616;
        width: 32px;
      }
    }
  }
`;

const ArrowButton = styled.button`
  box-sizing: border-box;
  position: absolute;
  width: 80px;
  height: 80px;
  top: calc(50% - 40px);
  background: #ffffff;
  border: 1px solid #d9dbde;
  box-shadow: 0px 4px 18px 5px rgba(43, 43, 43, 0.05);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease,
    background-color 0.2s ease;
  padding: 0;
  outline: none;

  &:hover {
    background-color: #fafafa;
    transform: scale(1.04);
    box-shadow: 0px 6px 20px 6px rgba(43, 43, 43, 0.08);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 1200px) {
    display: none;
  }
`;

const LeftArrow = styled(ArrowButton)`
  left: -40px;
  z-index: 3;
`;

const RightArrow = styled(ArrowButton)`
  right: -40px;
  z-index: 3;
`;

const FadeOverlay = styled.div`
  position: absolute;
  top: 0;
  width: 96px;
  height: 321px;
  background: url("/static/fade.svg") no-repeat;
  background-size: 100% 100%;
  pointer-events: none;
  z-index: 2;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const LeftFade = styled(FadeOverlay)`
  left: -40px;
`;

const RightFade = styled(FadeOverlay)`
  right: -40px;
  transform: scaleX(-1);
`;

const StarIcon = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="#FFAD00"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

const MobileScrollContainer = styled.div`
  display: none;

  @media (max-width: 1200px) {
    display: flex;
    flex-direction: row;
    width: 196px;
    overflow-x: auto;
    gap: 8px;
    flex-shrink: 0;

    /* Hide scrollbars */
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

export const RatingCard = () => {
  const { t } = useTranslation("icth-retail-main");
  const carouselRef = React.useRef<any>(null);

  const rawReviews = t("rating.reviews", { returnObjects: true });
  const reviewsList = Array.isArray(rawReviews) ? rawReviews : [];

  const handlePrev = () => {
    carouselRef.current?.prev();
  };

  const handleNext = () => {
    carouselRef.current?.next();
  };

  return (
    <MainContainer>
      <RatingContainer className="rating-container">
        <RatingBox className="rating-box">
          <RatingInfo className="rating-info">
            <RatingScore className="rating-score">
              {t("rating.score") || "4.3"}
            </RatingScore>
            <StarRating className="star-rating star-lg">
              <div className="desktop-stars">
                <Image
                  alt="rating stars"
                  src="/static/rating.svg"
                  width={284}
                  height={57}
                />
              </div>
              <div className="mobile-stars">
                <StarIcon size={21} />
                <StarIcon size={21} />
                <StarIcon size={21} />
                <StarIcon size={21} />
                <StarIcon size={21} />
              </div>
            </StarRating>
          </RatingInfo>
          <RatingHeadline className="rating-headline">
            {t("rating.title")}
          </RatingHeadline>
        </RatingBox>

        <TestimonialWrapper>
          <LeftFade />
          <LeftArrow onClick={handlePrev} aria-label="Previous slide">
            <ArrowLeftIcon />
          </LeftArrow>
          <TestimonialContainer className="testimonial-container">
            <Carousel
              ref={carouselRef}
              slidesToShow={3}
              centerMode
              centerPadding="78px"
              infinite
              dots
              autoplay
              autoplaySpeed={4000}
              responsive={[
                {
                  breakpoint: 1400,
                  settings: {
                    slidesToShow: 3,
                    centerMode: true,
                    centerPadding: "78px",
                  },
                },
                {
                  breakpoint: 1200,
                  settings: {
                    slidesToShow: 2,
                    centerMode: true,
                    centerPadding: "20px",
                  },
                },
                {
                  breakpoint: 800,
                  settings: {
                    slidesToShow: 1,
                    centerMode: true,
                    centerPadding: "40px",
                  },
                },
              ]}
            >
              {reviewsList.map((review) => (
                <CommentCard
                  key={review.commenter}
                  comment={`"${review.comment}"`}
                  commenter={review.commenter}
                  career={review.location}
                />
              ))}
            </Carousel>
          </TestimonialContainer>

          <MobileScrollContainer>
            {reviewsList.map((review) => (
              <CommentCard
                key={review.commenter}
                comment={`"${review.comment}"`}
                commenter={review.commenter}
                career={review.location}
              />
            ))}
          </MobileScrollContainer>

          <RightFade />
          <RightArrow onClick={handleNext} aria-label="Next slide">
            <ArrowRightIcon />
          </RightArrow>
        </TestimonialWrapper>
      </RatingContainer>
    </MainContainer>
  );
};

export default RatingCard;
